"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { request } from "../drills/types";
import { layoutConditionGroup, skillPickerSections } from "./picker-sections";
import type { Skill, SkillCategories } from "./types";
import "./skills.css";

export function useSkillCategories(admin = false) {
  const [categories, setCategories] = useState<SkillCategories>();
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt retries a failed catalog request.
  useEffect(() => {
    let active = true;
    setError("");
    request<SkillCategories>(admin ? "/api/admin/skills" : "/api/skills")
      .then((data) => {
        if (active) setCategories(data);
      })
      .catch((cause: Error) => {
        if (active) setError(cause.message);
      });
    return () => {
      active = false;
    };
  }, [attempt, admin]);
  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return { categories, error, reload };
}

export function SkillError({ message }: { message: string }) {
  const authoring = useAuthoringScope();
  return (
    <div className="skill-error" role="alert">
      <p>{message}</p>
      {message.includes("ログイン") && (
        <a href={authoring.href("/login")} target="_blank" rel="noreferrer">
          別のタブでログイン
        </a>
      )}
    </div>
  );
}

export function SkillPicker({
  categories,
  selectedIds,
  selectedSkills = [],
  onChange,
  disabled = false,
  maximum = 50,
}: {
  categories: SkillCategories;
  selectedIds: string[];
  selectedSkills?: Skill[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  maximum?: number;
}) {
  const [query, setQuery] = useState("");
  const searchId = useId();
  const conditionsId = useId();
  const conditionsHeadingId = useId();
  const [conditionsExpanded, setConditionsExpanded] = useState(() =>
    [
      ...categories.flatMap((category) => category.skills),
      ...selectedSkills,
    ].some(
      (skill) =>
        skill.groupCode === layoutConditionGroup &&
        selectedIds.includes(skill.id),
    ),
  );
  const [activeCategory, setActiveCategory] = useState<{
    id: string;
    trigger: HTMLButtonElement;
  } | null>(null);
  const all = [
    ...categories.flatMap((category) => category.skills),
    ...selectedSkills,
  ];
  const selected = selectedIds.flatMap((id) => {
    const skill = all.find((item) => item.id === id);
    return skill ? [skill] : [];
  });
  const selectedConditionCount = selected.filter(
    (skill) => skill.groupCode === layoutConditionGroup,
  ).length;
  const {
    mainCategories,
    conditionCategories,
    hasConditionCategories,
    isSearching,
  } = skillPickerSections(categories, query);
  function openCategory(id: string, trigger: HTMLButtonElement) {
    setActiveCategory({ id, trigger });
  }
  const openedCategory = categories.find(
    (category) => category.id === activeCategory?.id,
  );
  function toggle(id: string) {
    if (
      disabled ||
      (!selectedIds.includes(id) && selectedIds.length >= maximum)
    )
      return;
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((value) => value !== id)
        : [...selectedIds, id],
    );
  }
  return (
    <div className="skill-picker">
      <div className="skill-picker-selected">
        {selected.length === 0 ? (
          <p>スキルまたは配置条件を選んでください。組み合わせもできます。</p>
        ) : (
          selected.map((skill) => (
            <button
              key={skill.id}
              type="button"
              className="skill-chip"
              disabled={disabled}
              onClick={() => toggle(skill.id)}
              aria-label={`${skill.categoryLabel}の${skill.label}を外す`}
              title={skill.categoryLabel}
            >
              {skill.label}
              {!skill.isActive && "（利用終了）"}
              <span aria-hidden="true">×</span>
            </button>
          ))
        )}
      </div>
      <label className="skill-search" htmlFor={searchId}>
        スキル・配置条件を検索
        <input
          id={searchId}
          type="search"
          placeholder="例：サイド、逆ひねり、緊張"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
          }}
          disabled={disabled}
        />
      </label>
      <p className="skill-picker-count" role="status">
        {selectedIds.length} / {maximum}件選択
        {selectedConditionCount > 0 &&
          `（配置条件 ${selectedConditionCount}件）`}
      </p>
      {mainCategories.length > 0 && (
        <SkillCategoryButtons
          categories={mainCategories}
          selectedIds={selectedIds}
          disabled={disabled}
          onOpen={openCategory}
        />
      )}
      {hasConditionCategories &&
        (!isSearching || conditionCategories.length > 0) && (
          <section
            className="skill-conditions"
            aria-labelledby={conditionsHeadingId}
          >
            {isSearching ? (
              <h3 id={conditionsHeadingId} className="skill-conditions-heading">
                配置条件
              </h3>
            ) : (
              <h3 id={conditionsHeadingId} className="skill-conditions-heading">
                <button
                  type="button"
                  className="skill-conditions-toggle"
                  aria-expanded={conditionsExpanded}
                  aria-controls={conditionsId}
                  disabled={disabled}
                  onClick={() => setConditionsExpanded((value) => !value)}
                >
                  <span>配置条件を追加（任意）</span>
                  <span className="skill-category-meta">
                    {selectedConditionCount > 0 && (
                      <span className="skill-category-count">
                        {selectedConditionCount}件選択
                      </span>
                    )}
                    <span aria-hidden="true">
                      {conditionsExpanded ? "−" : "+"}
                    </span>
                  </span>
                </button>
              </h3>
            )}
            <div
              id={conditionsId}
              className="skill-conditions-content"
              hidden={!isSearching && !conditionsExpanded}
            >
              <p className="skill-picker-count">配置条件だけでも選べます。</p>
              <SkillCategoryButtons
                categories={conditionCategories}
                selectedIds={selectedIds}
                disabled={disabled}
                onOpen={openCategory}
              />
            </div>
          </section>
        )}
      {mainCategories.length === 0 && conditionCategories.length === 0 && (
        <p className="skill-picker-count">
          一致するスキル・配置条件はありません。
        </p>
      )}
      {activeCategory && openedCategory && (
        <SkillCategoryDialog
          category={openedCategory}
          initialQuery={query}
          selectedIds={selectedIds}
          onToggle={toggle}
          onDismiss={() => setActiveCategory(null)}
          returnFocus={activeCategory.trigger}
          disabled={disabled}
          maximum={maximum}
        />
      )}
    </div>
  );
}

function SkillCategoryButtons({
  categories,
  selectedIds,
  disabled,
  onOpen,
}: {
  categories: SkillCategories;
  selectedIds: string[];
  disabled: boolean;
  onOpen: (id: string, trigger: HTMLButtonElement) => void;
}) {
  return (
    <div className="skill-categories">
      {categories.map((category) => {
        const count = category.skills.filter((skill) =>
          selectedIds.includes(skill.id),
        ).length;
        return (
          <button
            key={category.id}
            type="button"
            className="skill-category"
            aria-haspopup="dialog"
            disabled={disabled}
            onClick={(event) => onOpen(category.id, event.currentTarget)}
          >
            <span>{category.label}</span>
            <span className="skill-category-meta">
              {count > 0 && (
                <span className="skill-category-count">{count}件</span>
              )}
              <span aria-hidden="true">›</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SkillCategoryDialog({
  category,
  initialQuery,
  selectedIds,
  onToggle,
  onDismiss,
  returnFocus,
  disabled,
  maximum,
}: {
  category: SkillCategories[number];
  initialQuery: string;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onDismiss: () => void;
  returnFocus: HTMLButtonElement;
  disabled: boolean;
  maximum: number;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const searchId = useId();
  const [query, setQuery] = useState(initialQuery);
  const normalized = query.trim().toLocaleLowerCase("ja");
  const shown = category.skills.filter((skill) =>
    `${category.label} ${skill.label} ${skill.description ?? ""}`
      .toLocaleLowerCase("ja")
      .includes(normalized),
  );
  const categoryCount = category.skills.filter((skill) =>
    selectedIds.includes(skill.id),
  ).length;
  useEffect(() => {
    const element = dialog.current;
    const overflow = document.body.style.overflow;
    element?.showModal();
    document.body.style.overflow = "hidden";
    heading.current?.focus({ preventScroll: true });
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      if (returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
    };
  }, [returnFocus]);
  return (
    <dialog
      ref={dialog}
      className="skill-category-dialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDismiss();
      }}
      onClose={(event) => {
        event.stopPropagation();
        // Ignore an old close event if the effect has already reopened the dialog.
        if (!event.currentTarget.open) onDismiss();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        // This dialog can sit inside the weakness or drill editing form.
        if (
          event.key === "Enter" &&
          !(event.target instanceof HTMLButtonElement)
        )
          event.preventDefault();
      }}
    >
      <div className="skill-category-dialog-heading">
        <h2 id={titleId} ref={heading} tabIndex={-1}>
          {category.label}
        </h2>
        <button type="button" aria-label="選択を閉じる" onClick={onDismiss}>
          ×
        </button>
      </div>
      <p id={descriptionId} className="skill-category-dialog-description">
        複数選択できます。選択内容は、閉じてもそのまま残ります。
      </p>
      <label className="skill-search" htmlFor={searchId}>
        このカテゴリを検索
        <input
          id={searchId}
          type="search"
          placeholder="項目名で検索"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          disabled={disabled}
        />
      </label>
      <div className="skill-category-dialog-options">
        <div className="skill-options">
          {shown.map((skill) => (
            <label className="skill-option" key={skill.id}>
              <input
                type="checkbox"
                checked={selectedIds.includes(skill.id)}
                disabled={
                  disabled ||
                  (!selectedIds.includes(skill.id) &&
                    selectedIds.length >= maximum)
                }
                onChange={() => onToggle(skill.id)}
              />
              <span>
                {skill.label}
                {skill.description && <small>{skill.description}</small>}
              </span>
            </label>
          ))}
        </div>
        {shown.length === 0 && (
          <p className="skill-category-dialog-empty">
            一致する項目はありません。
          </p>
        )}
      </div>
      <div className="skill-category-dialog-footer">
        <p className="skill-picker-count" role="status">
          このカテゴリ：{categoryCount}件選択
          <span>
            全体：{selectedIds.length} / {maximum}件
          </span>
          {selectedIds.length >= maximum && (
            <span>追加するには、選択済みの項目を外してください。</span>
          )}
        </p>
        <button type="button" className="primary-button" onClick={onDismiss}>
          選択を完了
        </button>
      </div>
    </dialog>
  );
}
